import { TFileTab } from "@/zapi-admin/shared/components/MonacoTabs/ui/MonacoTabs";
import { v4 } from "uuid";
import get from "lodash/get";
import { isAtomic, jsonify, TJsonifyObj } from "@/zapi-admin/shared/utils/json";

export type TRelevantFieldsVariant = "request" | "response";

const paths: Record<
  TRelevantFieldsVariant,
  (string | { label: string; resolver: string[] | ((data: TJsonifyObj) => string | undefined) })[]
> = {
  request: [
    "text",
    "prompt",
    "request.body.messages.0.content",
    "steps.0.request.body.input.0.content.0.text",
    {
      label: "Exact user message",
      resolver: ["requestAnnotations.userMessage"]
    },
    {
      label: "Last message",
      resolver: (data) => {
        if (typeof data !== "object") return undefined;
        if (!("messages" in data)) return undefined;
        const messages = data?.messages;
        if (Array.isArray(messages)) {
          return messages.at(-1).content;
        }
        return undefined;
      }
    },
    "input"
  ],
  response: [
    "text",
    {
      label: "AI Answer",
      resolver: (data) => {
        const content = get(data, "steps.0.content") as unknown;
        if (Array.isArray(content)) {
          console.log("is array");
          const texts = (content as Record<string, unknown>[]).map((item) => item?.text).filter(Boolean);
          if (texts.length > 0) return texts.join("\n");
        }

        const fallbackPaths = [
          "choices.0.message.content",
          "choices.0.message.function_call.arguments",
          "choices.0.message.tool_calls.0.function.arguments",
          "steps.0.content.0.content.0.text",
          "steps.0.response.body.choices.0.message.content",
          "steps.0.response.body.choices.0.message.function_call.arguments",
          "steps.0.response.body.choices.0.message.tool_calls.0.function.arguments",
          "steps.0.response.choices.0.message.content",
          "steps.0.response.body.choices.0.content",
          "steps.0.response.messages.0.content.0.text",
          "steps.0.response.body.output.0.content.0.text",
          "steps.0.response.body.output.1.content.0.text",
          "steps.0.response.messages.0.content.2.text",
          "steps.0.response.body.candidates.0.content.parts.0.text"
        ];
        for (const p of fallbackPaths) {
          const v = get(data, p);
          if (v !== undefined && v !== null) return typeof v === "object" ? JSON.stringify(v) : String(v);
        }
        return undefined;
      }
    },
    "choices.0.message.content.content",
    "choices.0.message.tool_calls.0.function.arguments.businessModels.0.textualDescription"
  ]
};

export function getRelevantFieldsPrettyfied(
  obj: object | string,
  variant: TRelevantFieldsVariant,
  logType?: string
): TFileTab[] {
  const tabs: TFileTab[] = [];

  if (!obj) return tabs;

  if (typeof obj === "string") {
    tabs.push({ id: v4(), label: "root", value: obj, language: "plaintext" });
    return tabs;
  }

  const safeObj = Object.freeze(jsonify(obj));

  if (logType === "ai-sdk-object" && variant === "response") {
    const objectContent = get(safeObj, "object");
    if (objectContent && typeof objectContent === "object") {
      tabs.push({
        id: v4(),
        label: "AI SDK Object",
        value: JSON.stringify(objectContent, null, 2),
        language: "json"
      });
    }
  }

  let pathToUse = "";
  for (const path of paths[variant]) {
    let val = null;
    if (typeof path === "string") {
      val = get(safeObj, path);
      pathToUse = path;
    } else if (Array.isArray(path.resolver)) {
      val = get(safeObj, path.resolver);
      for (const p of path.resolver) {
        const v = get(safeObj, p);
        if (v !== undefined && v !== null) {
          val = v;
          pathToUse = p;
          break;
        }
      }
    } else {
      val = path.resolver(safeObj);
    }
    if (isAtomic(val) || val === null || typeof val === "boolean" || typeof val === "object") {
      const label = typeof path === "string" ? pathToUse : path.label;
      tabs.push({
        id: `tab-${label}`,
        label,
        value: typeof val === "object" ? JSON.stringify(val) : String(val),
        language: "plaintext"
      });
    }
  }
  return tabs;
}
